import { computed, getCurrentInstance, toRefs } from 'vue-demi';

import { useCommonProps } from './use-common-props';
import { useDialerStore } from './use-dialer-store';

export const useAgentActionsProps = (props: {
  actionsType: 'invite' | 'transfer';
}) => {
  const vm = getCurrentInstance()!;
  const store = useDialerStore();
  const { actionsType } = toRefs(props);

  const title = computed(() => {
    switch (actionsType.value) {
      case 'transfer':
        return vm.proxy.$t('hitl.modules.dialer.transfer-popup-title');
      case 'invite':
      default:
        return vm.proxy.$t('hitl.modules.dialer.invite-popup-title');
    }
  });

  const showAgentsTab = computed(() => {
    switch (actionsType.value) {
      case 'invite':
        return !!store.controls.invite?.allowAgent;
      case 'transfer':
        return !!store.controls.transfer?.allowAgent;
      default:
        return false;
    }
  });

  const content = computed(() => {
    switch (actionsType.value) {
      case 'invite':
        return store.tabs.invite;
      case 'transfer':
        return store.tabs.transfer;
      default:
        return [];
    }
  });

  const context = computed(() => {
    return { sessionId: store.sessionId };
  });

  const canInviteThirdParty = computed(() => {
    switch (actionsType.value) {
      case 'invite':
        return !!store.controls.invite?.allow3rdParty;
      case 'transfer':
        return !!store.controls.transfer?.allow3rdParty;
      default:
        return false;
    }
  });

  const defaultSearchOptions = computed(() => {
    switch (actionsType.value) {
      case 'invite':
        return store.controls.invite?.agentSearchParams?.searchOptions;
      case 'transfer':
        return store.controls.transfer?.agentSearchParams?.searchOptions;
      default:
        return;
    }
  });

  const canChangeSearchOptions = computed(() => {
    switch (actionsType.value) {
      case 'invite':
        return store.controls.invite?.agentSearchParams?.canChangeSearchOptions ?? true;
      case 'transfer':
        return store.controls.transfer?.agentSearchParams?.canChangeSearchOptions ?? true;
      default:
        return true;
    }
  });

  return {
    title,
    showAgentsTab,
    content,
    context,
    canInviteThirdParty,
    defaultSearchOptions,
    canChangeSearchOptions,
  };
};

export function useAgentsActions(args: {
  actionsType: 'invite' | 'transfer';
}) {
  const props = useCommonProps();
  const store = useDialerStore();
  const { actionsType } = toRefs(args);

  const onInviteAgent = async (args: { agentId: string; }) => {
    try {
      store.isPerformingAction = true;
      await props.applyCommand({
        sessionId: store.sessionId,
        command: 'transfer',
        data: { agentId: args.agentId, type: actionsType.value },
      });
    } catch (e) {
      console.error('Failed to add an agent!', e);
    } finally {
      store.isPerformingAction = false;
    }
  };

  const onInvite3rdParty = async (args: { identifier: string; }) => {
    try {
      store.isPerformingAction = true;
      await props.applyCommand({
        sessionId: store.sessionId,
        command: 'add_3rdparty',
        data: { number: args.identifier, type: actionsType.value },
      });
    } catch (e) {
      console.error('Failed to add 3rd party member!', e);
    } finally {
      store.isPerformingAction = false;
    }
  };

  return {
    onInviteAgent,
    onInvite3rdParty,
  };
}
